import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, TemplateRef, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CanPipe } from 'ability';
import { isEqual } from 'lodash';
import { Alert, AlertService, ButtonComponent, MbsPopupType, MbsSize, bytesFrom } from 'mbs-ui-kit';
import { RoutingPath } from 'mbs-ui-shared/src/lib/constants/app-routing-path.enum';
import { POST_PAYMENT_TOKEN } from 'mbs-ui-shared/src/lib/tokens/post-payment.token';
import { PaymentMethodButtonComponent } from 'mbs-ui-shared@components/licenses/components/payment-method-button/payment-method-button.component';
import { UnifiedBillingAccount, UnifiedBillingAccountType } from 'mbs-ui-shared@models/storage-accounts/UnifiedBillingAccount';
import { AppPersistentStateService } from 'mbs-ui-shared@services/app-persistent-state.service';
import { StorageAccountsService } from 'mbs-ui-shared@services/storage-accounts/storage-accounts.service';
import { DateTimeUtils } from 'mbs-ui-shared@utils/DateTimeUtils';
import moment from 'moment';
import 'moment-duration-format';
import { Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { UbSubscribeService } from '../ub-subscribe.service';

type UbAlertContext = {
  daysFromCreate: number;
  durationToRemoveOrExpire: string;
  totalUsageLimit: number;
  storage: UnifiedBillingAccount;
};

@UntilDestroy()
@Component({
  selector: 'mbs-ub-notifications',
  templateUrl: './ub-notifications.component.html',
  styles: [],
  imports: [ButtonComponent, CommonModule, PaymentMethodButtonComponent, CanPipe]
})
export class UbNotificationsComponent implements AfterViewInit {
  private storageService = inject(StorageAccountsService);
  private alertService = inject(AlertService);
  private appStorage = inject(AppPersistentStateService);
  private router = inject(Router);
  ubSubscribe = inject(UbSubscribeService);
  isPostPayment = inject<Observable<boolean>>(POST_PAYMENT_TOKEN);

  readonly size = MbsSize;

  readonly NEED_TO_SHOW_TRIAL_ALERT_DAYS_COUNT = 7;
  @ViewChild('alertTemplate', { static: true }) alertTemplate: TemplateRef<any>;
  private readonly alertIdPrefix = 'ub-notification';

  public UnifiedBillingAccountType = UnifiedBillingAccountType;

  private existsAlertIds: string[] = [];

  ngAfterViewInit(): void {
    const userAccountAge = DateTimeUtils.getDaysCountBetweenDates(new Date(), new Date(this.appStorage.data.signUpDate));
    this.storageService.getUnifiedBillings();
    this.storageService.unifiedBillings$
      .pipe(
        distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
        untilDestroyed(this)
      )
      .subscribe(({ items: storages }) => {
        // remove all UB alerts before
        this.existsAlertIds.forEach((alertId) => this.alertService.clearById(alertId));
        this.existsAlertIds = [];

        for (const storage of storages) {
          const alertId = this.alertIdPrefix + storage.id;

          if (userAccountAge < this.NEED_TO_SHOW_TRIAL_ALERT_DAYS_COUNT) {
            this.appStorage.data.showUbMessage[storage.id] = false;
            continue;
          }

          if (this.appStorage.data.showUbMessage[storage.id] === false) {
            continue;
          }

          const ubContext = this.updateMessage(storage);

          if (ubContext != null) {
            this.alertService
              .alert(
                new Alert({
                  content: this.alertTemplate,
                  context: ubContext,
                  keepAfterRouteChange: true,
                  type: MbsPopupType.danger,
                  id: alertId
                })
              )
              .subscribe(() => {
                this.appStorage.data.showUbMessage = { ...this.appStorage.data.showUbMessage, [storage.id]: false };
              });

            this.existsAlertIds.push(alertId);
          }
        }
      });
  }

  updateMessage(storage: UnifiedBillingAccount): UbAlertContext {
    if (!storage) {
      return null;
    }

    const ubContext: Partial<UbAlertContext> = { storage };

    const daysFromCreate = moment.duration(moment().diff(moment.utc(storage.createDate, 'YYYY-MM-DD'))).asDays();
    ubContext.daysFromCreate = daysFromCreate;

    // also should same in ub-cancel-deletion.component.ts
    const durationToRemoveOrExpire = moment.duration(storage.usageTypeDaysCount, 'days').format('d [days]');
    ubContext.durationToRemoveOrExpire = durationToRemoveOrExpire;

    const totalUsageLimit = 2 * bytesFrom('TB');
    ubContext.totalUsageLimit = storage.ubTrialLimitSize || totalUsageLimit;

    const hidden =
      [UnifiedBillingAccountType.Paid, UnifiedBillingAccountType.Undefined].includes(storage.usageType) ||
      ([UnifiedBillingAccountType.TrialDeactivated, UnifiedBillingAccountType.PaidWithDebtDeactivated].includes(storage.usageType) &&
        storage.usageTypeDaysCount === 0) ||
      (this.ubSubscribe.deletedTypes.includes(storage.usageType) &&
        UnifiedBillingAccountType.PaidWithDebtManuallyDeactivated !== storage.usageType &&
        storage.usageTypeDaysCount === 0);

    return hidden ? null : (ubContext as UbAlertContext);
  }

  handleSubscribe(storage: UnifiedBillingAccount): void {
    this.ubSubscribe.showSwitchCommercial(storage);
  }

  handleGoLicensesPage(): void {
    this.router.navigate([RoutingPath.ApLicenses], { queryParams: { renew: true } });
  }
}
